import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";

// STYLES
import "./LoyaltyOfferLander.scss";

// STATIC
import headerIcon from "../../../../static/img/img-ihr-by-bodymatter-hr-logo-black-sm@3x.png";
import heroImage from "../../../../static/img/img-finger-scan-lm@3x.png";
import ihrLogo from "../../../../static/img/img-ihr-name-logo-black.svg";

import { ReactComponent as ProfileIcon } from "../../icons/profile.svg";

// HOOKS
import { useAuthContext } from "../../../../hooks/context/AuthContext";
import { useParams } from "react-router-dom";
import { useNavigate, useSearchParams } from "react-router-dom";

// COMPONENTS
import LoyaltyOfferCheckoutCardForm from "../../inputs/LoyaltyOfferCheckoutCardForm";
import LoadingSpinner from "../../modals/Spinners/LoadingSpinner";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import PremiumCalloutSummary from "../../callouts/PremiumCalloutSummary";

// ANALYTICS
import { analyticsSetUser, analyticsTrack } from "../../../../utils/amplitude";

// IMPORTANT - this must be at module level (see docs)
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);
const priceId = process.env.REACT_APP_STRIPE_2999_YEARLY_PRICE;
const STRIPE_PRICE_TO_PRODUCT: Record<string, string> = {
  // YEARLY
  [process.env.REACT_APP_STRIPE_2999_YEARLY_PRICE!]:
    "io.bodymatter.premium.instantheartratefree.1y.30usd.20250501",
};

// APPROVED VARIANTS
const APPROVED_VARIANTS = new Set<string>([
  "go_premium_default",
  "loyalty_offer_1",
]);
const DEFAULT_VARIANT = "go_premium_default";

const ISO_MILLIS_RE =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})\.(\d{3})$/;

const isoMillisToUtcMs = (ts: string): number | null => {
  const m = ISO_MILLIS_RE.exec(ts);
  if (!m) return null;

  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const h = Number(m[4]);
  const mi = Number(m[5]);
  const s = Number(m[6]);
  const ms = Number(m[7]);

  const utcMs = Date.UTC(y, mo - 1, d, h, mi, s, ms);
  return Number.isFinite(utcMs) ? utcMs : null;
};

const LoyaltyOfferLander: React.FC = () => {
  const navigate = useNavigate();
  const { user_id } = useParams<{ user_id: string }>();
  const [showPaymentSpinner, setShowPaymentSpinner] = useState<boolean>(false);
  const [paymentFailed, setPaymentFailed] = useState(false);

  const [hoursLeft, setHoursLeft] = useState("00");
  const [minutesLeft, setMinutesLeft] = useState("00");
  const [secondsLeft, setSecondsLeft] = useState("00");

  const userId = user_id ?? "";
  const viewedEventSentRef = useRef(false);

  const {
    token,
    eligibility,
    fetchEligibility,
    postSubscriptionsCreate,
    getStripeSubscriptionInfo,
    isPremium,
    email: acctEmail,
  } = useAuthContext();

  const [searchParams] = useSearchParams();

  const { promoPageVariant, isVariantApproved } = useMemo(() => {
    const raw = (
      searchParams.get("offer_variant") || // new
      searchParams.get("v") || // old
      ""
    )
      .trim()
      .toLowerCase();

    const safe = raw.replace(/[^a-z0-9_-]/g, "");

    if (APPROVED_VARIANTS.has(safe)) {
      return { promoPageVariant: safe, isVariantApproved: true };
    }

    return { promoPageVariant: DEFAULT_VARIANT, isVariantApproved: false };
  }, [searchParams]);

  const emailDisplay = useMemo<string>(() => {
    if (!isVariantApproved) return " ";

    const rawParam =
      searchParams.get("user_email") || // new
      searchParams.get("email"); // old

    if (!rawParam) return " ";

    const raw = decodeURIComponent(String(rawParam)).trim();
    if (!raw || raw === "null" || raw === "undefined") return " ";

    const at = raw.indexOf("@");
    if (at <= 0 || at === raw.length - 1) return " ";

    const local = raw.slice(0, at);
    const domain = raw.slice(at + 1);

    const maskedLocal = local.length > 10 ? `${local.slice(0, 10)}***` : local;
    return `${maskedLocal}@${domain}`.slice(0, 160);
  }, [searchParams, isVariantApproved]);

  const [EXPIRATION_UTC_MS, setEXPIRATION_UTC_MS] = useState<number | null>(null);
  const [expirationLoaded, setExpirationLoaded] = useState(false);

  const redirectToGoPremium = useCallback(
    (reason: string) => {
      const u = new URL(window.location.href);

      const replaced = u.pathname.replace(
        /(^|\/)loyalty-offer(\/|$)/,
        "$1go-premium$2",
      );

      u.pathname = replaced === u.pathname ? `/go-premium/${userId}` : replaced;
      u.searchParams.set("redirect_reason", reason);

      navigate(u.pathname + u.search, { replace: true });
    },
    [navigate, userId],
  );

  useEffect(() => {
    if (!expirationLoaded) return;

    const second = 1000;
    const minute = second * 60;
    const hour = minute * 60;
    const day = hour * 24;

    const updateCountdown = () => {
      const now = Date.now();
      const exp = EXPIRATION_UTC_MS;

      if (!exp || now > exp) {
        redirectToGoPremium("loyalty_offer_expired");
        return;
      }

      const msToExpiration = exp - now;

      const days = Math.floor(msToExpiration / day);
      const hours = Math.floor((msToExpiration % day) / hour) + days * 24;
      const minutes = Math.floor((msToExpiration % hour) / minute);
      const seconds = Math.floor((msToExpiration % minute) / second);

      setHoursLeft(String(hours).padStart(2, "0"));
      setMinutesLeft(String(minutes).padStart(2, "0"));
      setSecondsLeft(String(seconds).padStart(2, "0"));
    };

    updateCountdown();
    const id = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(id);
  }, [navigate, EXPIRATION_UTC_MS, expirationLoaded, redirectToGoPremium]);

  useEffect(() => {
    if (!userId || eligibility != null) return;

    (async () => {
      const result = await fetchEligibility(userId, "loyalty-offer");
      const offerExpiration =
        typeof (result as any)?.offerExpiration === "string"
          ? (result as any).offerExpiration
          : undefined;

      const expMs = offerExpiration ? isoMillisToUtcMs(offerExpiration) : null;

      if (!expMs) {
        redirectToGoPremium("loyalty_offer_expired");
        return;
      }

      setEXPIRATION_UTC_MS(expMs);
      setExpirationLoaded(true);
    })();
  }, [userId, fetchEligibility, eligibility, redirectToGoPremium]);

  useEffect(() => {
    if (eligibility === true && !viewedEventSentRef.current) {
      viewedEventSentRef.current = true;
      if (userId) analyticsSetUser(userId);
      analyticsTrack("loyaltyOffereWebPageViewed", {
        path: window.location.pathname,
        ts: Date.now(),
        promoPageVariant: promoPageVariant,
        fullQuery: window.location.search,
      });
    }
  }, [eligibility, userId, promoPageVariant]);

  useEffect(() => {
    if (eligibility === false) {
      navigate("/already-premium/", { replace: true });
    }
  }, [eligibility, isPremium, navigate]);

  return (
    <section className="LoyaltyOfferLander" aria-label="Go Premium">
      {emailDisplay && (
        <div
          className="LoyaltyOfferLander__emailBanner"
          role="note"
          aria-label="Email"
        >
          <img src={ihrLogo} alt="Instant Heart Rate" className="brand" />
          <div className="right">
            <ProfileIcon className="icon" />
            <span className="emailText">{emailDisplay}</span>
          </div>
        </div>
      )}

      <LoadingSpinner
        className="LoyaltyOfferLander__opaqueSpinner"
        raised={showPaymentSpinner}
        fullPage
      />

      <div className="LoyaltyOfferLander__content">
        <div className="LoyaltyOfferLander__contentWrapper">
          <h1 className="HRVCallout">Your Loyalty Offer</h1>
          <div
            className="countdown-timer-container"
            aria-label="Countdown timer"
          >
            <div className="cdt-left">
              <div className="cdt-label">EXPIRES IN</div>
            </div>

            <div className="cdt-right">
              <div className="cdt-right-top">
                <div className="cdt-hrs">{hoursLeft}</div>
                <div className="cdt-sep">:</div>
                <div className="cdt-min">{minutesLeft}</div>
                <div className="cdt-sep">:</div>
                <div className="cdt-sec">{secondsLeft}</div>
              </div>

              <div className="cdt-right-bottom" aria-hidden="true">
                <div>HRS</div>
                <div className="cdt-sep">:</div>
                <div>MIN</div>
                <div className="cdt-sep">:</div>
                <div>SEC</div>
              </div>
            </div>
          </div>

          <div className="LoyaltyOfferLander__heroWithSummary">
            <img
              src={heroImage}
              loading="lazy"
              alt="Instant Heart Rate"
              className="heroImage"
            />
            <div className="OrderSummaryOverlay">
              <div className="trialTab">50% Off Loyalty Offer</div>
              <div className="OrderSummaryWrapper">
                <PremiumCalloutSummary
                  copyTextUpper={
                    <>
                      <span
                        style={{
                          textDecoration: "line-through",
                          fontWeight: "normal",
                          marginRight: 4,
                        }}
                      >
                        $59.99
                      </span>
                      $29.99 USD / year
                    </>
                  }
                  copyTextLower="Cancel anytime."
                />
              </div>
            </div>
          </div>

          <p className="LoyaltyOfferLander__platform-disclaimer">
            This offer only valid for Apple devices. Subscription will
            automatically renew unless cancelled before the end of current
            period. Manage your subscription from reports.instantheartrate.com.
            Heart Age available only on supported devices. By proceeding, you
            agree to the{" "}
            <a href="https://instantheartrate.com/privacy-policy-and-terms-of-use#tos-section">
              Terms
            </a>{" "}
            and{" "}
            <a href="https://instantheartrate.com/privacy-policy-and-terms-of-use#privacy-policy-section">
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>

      <div className="LoyaltyOfferLander__checkout">
        <div className="LoyaltyOfferLander__checkoutWrapper">
          {eligibility ? (
            <Elements stripe={stripePromise} options={{ locale: "auto" }}>
              <LoyaltyOfferCheckoutCardForm
                email={acctEmail ?? ""}
                userId={userId}
                submissionErrorText={
                  paymentFailed ? "Payment failed, please try again later." : ""
                }
                onPaymentMethodCreated={(pm) => {
                  setShowPaymentSpinner(true);

                  const onSuccess = () => {
                    setPaymentFailed(false);

                    const productID =
                      (priceId && STRIPE_PRICE_TO_PRODUCT[priceId]) || undefined;

                    if (userId) {
                      analyticsSetUser(userId);
                    }

                    if (productID && userId) {
                      analyticsTrack("boughtIAP", {
                        productID,
                        path: window.location.pathname,
                        ts: Date.now(),
                        promoPageVariant,
                        fullQuery: window.location.search,
                      });
                    }

                    navigate("/checkout-success/", { replace: true });
                    setShowPaymentSpinner(false);
                    getStripeSubscriptionInfo(token!!);
                  };

                  const onFail = (error?: string) => {
                    if (error === "user is not eligible") {
                      navigate("/already-premium", { replace: true });
                    }
                    setPaymentFailed(true);
                    setShowPaymentSpinner(false);
                  };

                  if (priceId) {
                    postSubscriptionsCreate(userId, priceId, pm.id, onSuccess, onFail);
                  }
                }}
              />
            </Elements>
          ) : eligibility === null ? (
            <div className="LoyaltyOfferLander__loading">Loading...</div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default LoyaltyOfferLander;
